'use client';
import React, { useState, useRef, useEffect } from 'react';
import { Menu, Bell, Search, Moon, Sun, Plus, ChevronDown, Check, LogOut, User, Settings, CreditCard, HelpCircle, ArrowLeftRight } from 'lucide-react';
import Link from 'next/link';
import { useRouter } from 'next/navigation';
import { useCompany, Company } from '@/contexts/CompanyContext';
import { useAuth } from '@/contexts/AuthContext';
import CompanyLimitPopup from '@/components/CompanyLimitPopup';
import Icon from '@/components/ui/AppIcon';


interface TopbarProps {
  onToggleSidebar: () => void;
  onMobileMenuOpen: () => void;
  sidebarCollapsed: boolean;
}

function CompanySwitcher() {
  const { companies, activeCompany, setActiveCompany, subscription, canCreateCompany } = useCompany();
  const [open, setOpen] = useState(false);
  const [showLimitPopup, setShowLimitPopup] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const router = useRouter();

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const handleSelect = (company: Company) => {
    setActiveCompany(company);
    setOpen(false);
    router.refresh();
  };

  const handleAddNew = () => {
    setOpen(false);
    if (!canCreateCompany()) {
      setShowLimitPopup(true);
      return;
    }
    router.push('/select-company');
  };

  if (!activeCompany) return null;

  return (
    <>
      <div ref={ref} className="relative hidden md:block">
        <button
          onClick={() => setOpen(!open)}
          className="flex items-center gap-2 px-3 py-2 rounded-xl bg-secondary/60 hover:bg-secondary border border-border transition-colors max-w-[200px]"
        >
          {activeCompany.logoUrl ? (
            <img
              src={activeCompany.logoUrl}
              alt={`${activeCompany.companyName} logo`}
              className="w-5 h-5 rounded object-contain shrink-0"
            />
          ) : (
            <div
              className="w-5 h-5 rounded-md flex items-center justify-center text-white text-2xs font-800 shrink-0"
              style={{ backgroundColor: activeCompany.themeColor }}
            >
              {activeCompany.companyName.charAt(0).toUpperCase()}
            </div>
          )}
          <span className="text-sm font-600 text-foreground truncate max-w-[120px]">
            {activeCompany.companyName}
          </span>
          <ChevronDown size={13} className={`text-muted-foreground shrink-0 transition-transform ${open ? 'rotate-180' : ''}`} />
        </button>

        {open && (
          <div className="absolute left-0 top-full mt-2 w-72 bg-card border border-border rounded-xl shadow-xl z-50 overflow-hidden fade-in">
            <div className="px-4 py-3 border-b border-border">
              <p className="text-xs font-600 text-muted-foreground uppercase tracking-wider">Switch Company</p>
            </div>
            <div className="py-1 max-h-64 overflow-y-auto">
              {companies.map((company) => (
                <button
                  key={company.id}
                  onClick={() => handleSelect(company)}
                  className="w-full flex items-center gap-3 px-4 py-3 hover:bg-secondary transition-colors text-left"
                >
                  {company.logoUrl ? (
                    <img
                      src={company.logoUrl}
                      alt={`${company.companyName} logo`}
                      className="w-8 h-8 rounded-lg object-contain shrink-0 border border-border"
                    />
                  ) : (
                    <div
                      className="w-8 h-8 rounded-lg flex items-center justify-center text-white text-xs font-800 shrink-0"
                      style={{ backgroundColor: company.themeColor }}
                    >
                      {company.companyName.charAt(0).toUpperCase()}
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-600 text-foreground truncate">{company.companyName}</p>
                    <p className="text-2xs text-muted-foreground truncate">{company.companyType}</p>
                  </div>
                  {activeCompany.id === company.id && (
                    <Check size={14} className="text-primary shrink-0" />
                  )}
                </button>
              ))}
            </div>
            <div className="border-t border-border p-2">
              <button
                onClick={handleAddNew}
                className="w-full flex items-center gap-2.5 px-3 py-2.5 rounded-lg hover:bg-secondary transition-colors text-left"
              >
                <div className="w-8 h-8 rounded-lg border-2 border-dashed border-border flex items-center justify-center">
                  <Plus size={14} className={canCreateCompany() ? 'text-muted-foreground' : 'text-amber-500'} />
                </div>
                <div>
                  <span className={`text-sm font-600 ${canCreateCompany() ? 'text-primary' : 'text-amber-600 dark:text-amber-400'}`}>
                    Add New Company
                  </span>
                  {!canCreateCompany() && (
                    <p className="text-2xs text-muted-foreground">Limit reached · Upgrade to add more</p>
                  )}
                </div>
              </button>
            </div>
          </div>
        )}
      </div>

      {showLimitPopup && (
        <CompanyLimitPopup
          maxCompanies={subscription.maxCompanies}
          currentCount={companies.length}
          onClose={() => setShowLimitPopup(false)}
        />
      )}
    </>
  );
}

export default function Topbar({ onToggleSidebar, onMobileMenuOpen }: TopbarProps) {
  const [darkMode, setDarkMode] = useState(false);
  const [notifOpen, setNotifOpen] = useState(false);
  const [userOpen, setUserOpen] = useState(false);
  const userRef = useRef<HTMLDivElement>(null);
  const notifRef = useRef<HTMLDivElement>(null);
  const { user, signOut } = useAuth();
  const { activeCompany } = useCompany();
  const router = useRouter();

  const toggleDark = () => {
    setDarkMode(!darkMode);
    document.documentElement.classList.toggle('dark');
  };

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (userRef.current && !userRef.current.contains(e.target as Node)) setUserOpen(false);
      if (notifRef.current && !notifRef.current.contains(e.target as Node)) setNotifOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const handleSignOut = async () => {
    setUserOpen(false);
    await signOut();
    router.push('/sign-up-login-screen');
  };

  const userInitial = user?.email?.charAt(0).toUpperCase() || 'U';
  const userName = user?.user_metadata?.full_name || user?.email?.split('@')[0] || 'User';

  const profileMenuItems = [
    { id: 'my-profile', label: 'My Profile', icon: User, href: '/profile' },
    { id: 'company-settings', label: 'Company Settings', icon: Settings, href: '/settings' },
    { id: 'subscription', label: 'Subscription', icon: CreditCard, href: '/subscription' },
    { id: 'switch-company', label: 'Switch Company', icon: ArrowLeftRight, href: '/select-company' },
    { id: 'support', label: 'Support', icon: HelpCircle, href: '/support' },
  ];

  return (
    <header className="h-16 bg-card/80 glass border-b border-border flex items-center gap-3 px-4 lg:px-6 shrink-0 sticky top-0 z-20">
      {/* Mobile menu */}
      <button onClick={onMobileMenuOpen} className="btn-ghost p-2 lg:hidden">
        <Menu size={20} />
      </button>

      {/* Desktop sidebar toggle */}
      <button onClick={onToggleSidebar} className="btn-ghost p-2 hidden lg:flex">
        <Menu size={18} />
      </button>

      {/* Company Switcher */}
      <CompanySwitcher />

      {/* Search */}
      <div className="flex-1 max-w-md hidden sm:block">
        <div className="relative">
          <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <input
            type="text"
            placeholder="Search invoices, customers…"
            className="form-input pl-9 py-2 text-sm h-9 bg-secondary/60"
          />
          <span className="absolute right-3 top-1/2 -translate-y-1/2 text-2xs text-muted-foreground font-mono">⌘K</span>
        </div>
      </div>

      <div className="flex-1" />

      {/* Quick create */}
      <Link href="/invoice-generator" className="btn-primary py-2 px-3 text-sm hidden sm:flex gap-1.5">
        <Plus size={15} />
        New Invoice
      </Link>

      {/* Dark mode */}
      <button onClick={toggleDark} className="btn-ghost p-2">
        {darkMode ? <Sun size={18} /> : <Moon size={18} />}
      </button>

      {/* Notifications */}
      <div ref={notifRef} className="relative">
        <button
          onClick={() => setNotifOpen(!notifOpen)}
          className="btn-ghost p-2 relative"
        >
          <Bell size={18} />
        </button>
        {notifOpen && (
          <div className="absolute right-0 top-full mt-2 w-80 bg-card border border-border rounded-xl card-shadow-lg z-50 overflow-hidden fade-in">
            <div className="px-4 py-3 border-b border-border flex items-center justify-between">
              <span className="font-600 text-sm">Notifications</span>
            </div>
            <div className="flex flex-col items-center justify-center py-8 text-center px-4">
              <p className="text-sm font-600 text-foreground">No notifications</p>
              <p className="text-xs text-muted-foreground mt-1">You're all caught up!</p>
            </div>
          </div>
        )}
      </div>

      {/* User Profile Dropdown */}
      <div ref={userRef} className="relative">
        <button
          onClick={() => setUserOpen(!userOpen)}
          className="flex items-center gap-2 btn-ghost px-2 py-1.5"
        >
          {activeCompany?.logoUrl ? (
            <img
              src={activeCompany.logoUrl}
              alt="Company logo"
              className="w-7 h-7 rounded-full object-contain border border-border"
            />
          ) : (
            <div className="w-7 h-7 rounded-full bg-gradient-primary flex items-center justify-center text-white text-xs font-700">
              {userInitial}
            </div>
          )}
          <span className="text-sm font-500 hidden md:block">{userName}</span>
          <ChevronDown size={14} className="text-muted-foreground hidden md:block" />
        </button>
        {userOpen && (
          <div className="absolute right-0 top-full mt-2 w-56 bg-card border border-border rounded-xl shadow-xl z-50 overflow-hidden fade-in">
            {/* User info header */}
            <div className="px-4 py-3 border-b border-border">
              <p className="text-sm font-600 text-foreground truncate">{userName}</p>
              <p className="text-xs text-muted-foreground truncate">{user?.email}</p>
              {activeCompany && (
                <p className="text-xs text-primary truncate mt-0.5 font-500">{activeCompany.companyName}</p>
              )}
            </div>

            {/* Menu items */}
            <div className="py-1">
              {profileMenuItems.map((item) => {
                const Icon = item.icon;
                return (
                  <Link
                    key={item.id}
                    href={item.href}
                    onClick={() => setUserOpen(false)}
                    className="flex items-center gap-2.5 px-4 py-2.5 hover:bg-secondary transition-colors text-sm text-foreground"
                  >
                    <Icon size={15} className="text-muted-foreground" />
                    {item.label}
                  </Link>
                );
              })}
            </div>

            {/* Sign out — always last, always visible */}
            <div className="border-t border-border py-1">
              <button
                onClick={handleSignOut}
                className="w-full flex items-center gap-2.5 px-4 py-2.5 hover:bg-secondary transition-colors text-sm text-negative text-left"
              >
                <LogOut size={15} />
                Sign Out
              </button>
            </div>
          </div>
        )}
      </div>
    </header>
  );
}